import { ChangeDetectionStrategy, Component, WritableSignal, computed, input } from '@angular/core';

export type ButtonVariant = 'primary' | 'secondary' | 'primary-inverse' | 'ghost';
/** `responsive` is 44px tall below 1024px wide and 36px from 1024px, whatever the pointer. */
export type ButtonSize = 'default' | 'sm' | 'responsive';

export interface ButtonClassOptions {
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  default: 'h-control-md px-4 text-ui pointer-coarse:h-11 [&_svg]:size-4',
  sm: 'touch-hit h-control-sm px-3 text-caption [&_svg]:size-3.5',
  responsive: 'h-11 lg:h-[36px] px-4 text-ui lg:text-caption [&_svg]:size-4',
};

/**
 * The classes `app-button` draws with. Exported so a router link that must look like a button
 * (`<a routerLink>` can't be an `app-button`) matches it exactly; add a `gap-*` for its icon.
 */
export function buttonClasses({ variant, size, fullWidth = false, loading = false }: ButtonClassOptions): string {
  // While loading the button is disabled for interaction but keeps its variant colours,
  // so the disabled:* fill is only applied for a plain disabled button.
  const base = [
    'relative inline-flex items-center justify-center whitespace-nowrap cursor-pointer',
    'rounded-md border font-medium tracking-[-0.006em] transition-nike',
    'focus-visible:outline-none focus-visible:shadow-focus',
    loading ? 'cursor-progress' : 'disabled:cursor-not-allowed',
    fullWidth ? 'flex w-full' : 'w-auto',
    SIZE_CLASSES[size],
  ];

  switch (variant) {
    case 'primary':
      base.push('border-transparent bg-primary text-primary-inverse', 'hover:bg-primary-hover');
      break;
    case 'secondary':
      base.push(
        'bg-background text-primary border-border shadow-xs',
        'hover:bg-grey-50 hover:border-grey-400',
      );
      break;
    case 'primary-inverse':
      // For dark surfaces: disabled stays dark rather than turning light grey.
      base.push('border-transparent bg-background text-primary', 'hover:bg-grey-200');
      if (!loading) {
        base.push(
          'disabled:bg-grey-700 disabled:border-grey-700 disabled:text-text-disabled-inverse',
        );
      }
      break;
    case 'ghost':
      base.push('border-transparent bg-transparent text-primary', 'hover:bg-grey-100');
      if (!loading) {
        base.push('disabled:bg-transparent disabled:text-muted');
      }
      break;
  }

  if (!loading && (variant === 'primary' || variant === 'secondary')) {
    base.push(
      'disabled:bg-grey-100 disabled:border-grey-100 disabled:text-muted disabled:shadow-none',
    );
  }

  return base.join(' ');
}

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
  readonly type = input<'submit' | 'button' | 'reset'>('button');
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('default');
  readonly loading = input(false);
  readonly disabled = input(false);
  readonly fullWidth = input(false);
  /** Accessible name for the inner button when its text alone is ambiguous (e.g. repeated "Edit"). */
  readonly ariaLabel = input<string | null>(null);

  protected readonly isDisabled = computed(() => this.loading() || this.disabled());

  protected readonly contentGap = computed(() => (this.size() === 'sm' ? 'gap-1.5' : 'gap-2'));

  protected readonly classes = computed(() =>
    buttonClasses({
      variant: this.variant(),
      size: this.size(),
      fullWidth: this.fullWidth(),
      loading: this.loading(),
    }),
  );
}
